/**
 * Agent Detail Layout — shared header + tab navigation
 *
 * Route: /admin/reporting/agents/:id (layout)
 */

import { Outlet, Link } from '@tanstack/react-router'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { useAgentProfile } from '@/features/reporting/hooks/useReportingData'
import * as styles from '../index.css'

export const Route = createFileRoute({
  component: AgentDetailLayout,
})

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
  return `${Math.floor(diffDays / 365)} years ago`
}

const TAB_STYLE: React.CSSProperties = {
  padding: '8px 16px',
  textDecoration: 'none',
  fontSize: '14px',
  fontWeight: 500,
  borderBottom: '2px solid transparent',
  color: '#666',
}

const TAB_ACTIVE_STYLE: React.CSSProperties = {
  ...TAB_STYLE,
  color: '#027F87',
  borderBottomColor: '#027F87',
}

function AgentDetailLayout() {
  const { id } = Route.useParams()
  const { data: profile, isLoading } = useAgentProfile(id)

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Stack gap="16" align="center" justify="center" style={{ minHeight: '400px' }}>
            <LoadingSpinner size="md" />
          </Stack>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Link to="/admin/reporting/agents" search={{ tab: 'individual', sortBy: 'last_login', sortDir: 'desc' }} style={{ textDecoration: 'none', color: '#027F87', fontSize: '14px' }}>
            &larr; Back to Agents
          </Link>
          <Stack gap="16" align="center" justify="center" style={{ minHeight: '400px' }}>
            <Text variant="body" color="muted">Agent not found.</Text>
          </Stack>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Back Link */}
        <Link to="/admin/reporting/agents" search={{ tab: 'individual', sortBy: 'last_login', sortDir: 'desc' }} style={{ textDecoration: 'none', color: '#027F87', fontSize: '14px' }}>
          &larr; Back to Agents
        </Link>

        {/* Profile Header */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          <Stack gap="8">
            <Text as="h1" variant="h4" weight="bold">
              {profile.first_name} {profile.last_name}
            </Text>
            <Stack direction="row" gap="24" style={{ flexWrap: 'wrap' }}>
              {profile.email && (
                <Text variant="bodySm" color="muted">{profile.email}</Text>
              )}
              {profile.company_name && (
                <Text variant="bodySm" color="muted">{profile.company_name}</Text>
              )}
              {profile.market && (
                <Text variant="bodySm" color="muted">
                  Market: {profile.market}
                </Text>
              )}
              <Text variant="bodySm" color="muted">
                Last Login: {formatRelativeTime(profile.last_login_at)}
              </Text>
              {profile.signup_date && (
                <Text variant="bodySm" color="muted">
                  Signed Up: {new Date(profile.signup_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Text>
              )}
            </Stack>
            {profile.agent_type === 'external' && (
              <>
                <div style={{ borderTop: '1px solid #eee', marginTop: '4px' }} />
                <Stack direction="row" gap="24" style={{ flexWrap: 'wrap' }}>
                  <Text variant="bodySm" weight="bold">External Agent</Text>
                  {(profile.license_number || profile.license_state) && (
                    <Text variant="bodySm" color="muted">
                      License: {profile.license_number ?? '—'}
                      {profile.license_state ? ` (${profile.license_state})` : ''}
                    </Text>
                  )}
                  {profile.mls_boards && profile.mls_boards.length > 0 && (
                    <Text variant="bodySm" color="muted">
                      MLS: {profile.mls_boards.join(', ')}
                    </Text>
                  )}
                  {profile.partnership_source && (
                    <Text variant="bodySm" color="muted">
                      Source: {profile.partnership_source}
                    </Text>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #e0e0e0' }}>
          <span style={TAB_ACTIVE_STYLE}>
            Metrics
          </span>
          <span style={{ ...TAB_STYLE, opacity: 0.5, cursor: 'default' }}>
            Activity Log
          </span>
        </div>

        {/* Child route content */}
        <Outlet />
      </div>
    </div>
  )
}
